export interface PaymentScheduleItem {
  paymentNumber: number;
  dueDate: string;
  amount: number;
}

// "brokerage" is a UI-only branch in the Add-Contract wizard: selecting it
// swaps the form body for the brokerage upload wizard (which saves to the
// separate Nest brokerage-contracts slice). It is never sent to the regular
// createContract RPC — handleSubmit only runs for rent/sale/sublease.
export type ContractType = "rent" | "sale" | "sublease" | "brokerage";
// Sale contracts split into two structures (CEO 2026-07-23):
//   - "immediate": a normal transfer-of-ownership sale. No payment schedule,
//     no duration; the buyer pays the sale amount (minus any down payment) via
//     a single preferred payment channel.
//   - "offplan": sold before delivery. Follows the lease payment-schedule
//     structure (frequency, plan count, collection) and carries a delivery date
//     (the "end date" slot, relabelled). Only meaningful when contractType==="sale".
export type SaleType = "immediate" | "offplan";
export type PaymentFrequency = "monthly" | "quarterly" | "semi-annually" | "annually" | "custom";
export type PaymentMethod = "cash" | "bank_transfer" | "cheque" | "credit_card" | "ejar_network";
export type DurationUnit = "day" | "month" | "year";
export type UtilityType = "meter" | "fixed" | "included" | "not_available";
export type Payer = "owner" | "tenant";
export type FeeType = "percentage" | "fixed";

export interface UtilityConfig {
  type: UtilityType;
  amount?: string;
  payer: Payer;
}

export interface FeeConfig {
  type: FeeType;
  value: string;
  payer: Payer;
}

// Canonical list of per-unit services collected by EJAR. Keep in sync with
// the contract_units.service_fees JSONB shape and the RPC payload — single
// source of truth so adding another service touches one place.
//
// Two groups:
//   - "fees" (sewage / maintenance / cleaning / security): annual amounts.
//   - "utilities" (electricity / water / gas / internet): annual amounts +
//     a `type` (meter / fixed / included / not_available).
export const SERVICE_FEE_KEYS = [
  "sewage",
  "maintenance",
  "cleaning",
  "security",
] as const;

export const SERVICE_UTILITY_KEYS = [
  "electricity",
  "water",
  "gas",
  "internet",
] as const;

export const ALL_SERVICE_KEYS = [
  ...SERVICE_FEE_KEYS,
  ...SERVICE_UTILITY_KEYS,
] as const;

export type ServiceFeeKey = (typeof SERVICE_FEE_KEYS)[number];
export type ServiceUtilityKey = (typeof SERVICE_UTILITY_KEYS)[number];
export type AnyServiceKey = (typeof ALL_SERVICE_KEYS)[number];

// Bilingual labels for each service key.
export const SERVICE_LABELS: Record<AnyServiceKey, { ar: string; en: string }> = {
  sewage: { ar: "رسوم الصرف الصحي", en: "Sewage" },
  maintenance: { ar: "رسوم الصيانة", en: "Maintenance" },
  cleaning: { ar: "رسوم النظافة", en: "Cleaning" },
  security: { ar: "رسوم الأمن", en: "Security" },
  electricity: { ar: "الكهرباء", en: "Electricity" },
  water: { ar: "الماء", en: "Water" },
  gas: { ar: "الغاز", en: "Gas" },
  internet: { ar: "الإنترنت", en: "Internet" },
};

// Short variant used in table headers / breakdown rows.
export const SERVICE_SHORT_LABELS: Record<AnyServiceKey, { ar: string; en: string }> = {
  sewage: { ar: "صرف صحي", en: "Sewage" },
  maintenance: { ar: "صيانة", en: "Maintenance" },
  cleaning: { ar: "نظافة", en: "Cleaning" },
  security: { ar: "أمن", en: "Security" },
  electricity: { ar: "كهرباء", en: "Electricity" },
  water: { ar: "ماء", en: "Water" },
  gas: { ar: "غاز", en: "Gas" },
  internet: { ar: "إنترنت", en: "Internet" },
};

// Legacy aliases for compat with code that still imports the old names.
// Same data, scoped to the original 4 EJAR fees.
export const SERVICE_FEE_LABELS = SERVICE_LABELS;
export const SERVICE_FEE_SHORT_LABELS = SERVICE_SHORT_LABELS;

// Per-service entry on a contract unit. `amount` + `payer` apply to every
// service. `type` only applies to utilities (electricity/water/gas/internet)
// — the RPC ignores it for amount math, it's stored for EJAR / display.
export interface ContractServiceEntry {
  amount: string;        // empty string = not configured
  payer: Payer;          // tenant-paid amounts get folded into rent invoices
  type?: UtilityType;    // only meaningful for utility keys
}

export function emptyServiceEntry(opts?: { utility?: boolean; payer?: Payer }): ContractServiceEntry {
  return {
    amount: "",
    payer: opts?.payer ?? "tenant",
    ...(opts?.utility ? { type: "not_available" as const } : {}),
  };
}

// Per-unit row on a multi-unit contract. Each unit carries its own rent
// plus 8 services (4 EJAR fees + 4 utilities). Each service has amount,
// payer, and (for utilities) a type. Empty `amount` string = not configured.
export interface ContractUnitFormRow {
  unitId: string;
  rentAmount: string;
  serviceFees: Record<AnyServiceKey, ContractServiceEntry>;
}

export const emptyContractUnitRow: ContractUnitFormRow = {
  unitId: "",
  rentAmount: "",
  serviceFees: {
    sewage:      emptyServiceEntry(),
    maintenance: emptyServiceEntry(),
    cleaning:    emptyServiceEntry(),
    security:    emptyServiceEntry(),
    electricity: emptyServiceEntry({ utility: true }),
    water:       emptyServiceEntry({ utility: true }),
    gas:         emptyServiceEntry({ utility: true }),
    internet:    emptyServiceEntry({ utility: true }),
  },
};

/**
 * How the entered amount should be interpreted for schedule generation.
 *
 *  'term_total' (DEFAULT): the entered amount IS the full-term total for this
 *    contract — no proration is applied. This is the most common case and the
 *    safe default: the user enters exactly what the tenant owes in total.
 *
 *  'annual': the entered amount is a yearly rate. The full-term total is
 *    computed as fullTermTotal(enteredAmount, term), applying REGA's 364-day
 *    (52-week) partial-year proration for sub-year or non-whole-year terms.
 *
 * This value lives in wizard form state only; persistence to a DB column
 * (contracts.rent_basis) is deferred to PR2.
 */
// PR2: persist rentBasis to a contracts.rent_basis column + enforce server-side.
export type RentBasis = 'term_total' | 'annual';

export interface ContractWizardFormData {
  // Step 1: Contract Information
  contractType: ContractType;
  // Only meaningful when contractType === "sale". Drives the wizard step model
  // (immediate → no payment/services steps; offplan → lease-style payment step)
  // and the server-side schedule/collection generation.
  saleType: SaleType;
  propertyId: string;
  // Legacy single-unit field — kept for backward compatibility with code
  // that still reads form.unitId. For new contracts, we use `units` below
  // and treat units[0].unitId as the primary unit.
  unitId: string;
  // New: multi-unit rows. Always has at least one entry (single-unit case =
  // one entry; multi-unit case = N entries). All units must belong to
  // propertyId and share the same purpose (enforced by the unit picker UI).
  units: ContractUnitFormRow[];
  tenantId: string;
  startDate: string;
  durationValue: string; // numeric value like "12", "1", "180"
  durationUnit: DurationUnit; // "day" | "month" | "year"
  // Ejar-style Y/M/D duration entry (optional UI sugar — see DurationFields).
  // Two-way bound with startDate/endDate; durationValue/durationUnit above are
  // kept in sync from the actual dates so downstream schedule generation is
  // unaffected by which entry path the user took.
  durationYears: string;
  durationMonths: string;
  durationDays: string;
  endDate: string; // Auto-calculated from startDate + duration, or entered directly
  totalAmount: string;
  // For rent/sublease this is the security deposit. For SALE contracts it is
  // the buyer's DOWN PAYMENT, interpreted per `downPaymentType` (fixed SAR or a
  // % of the sale amount) and DEDUCTED from the total sale amount server-side.
  depositAmount: string;
  // How `depositAmount` is read on a SALE contract: a fixed SAR figure or a
  // percentage of totalAmount. Ignored for rent/sublease (always fixed deposit).
  downPaymentType: FeeType;

  // Fees (4 types)
  brokerageFee: FeeConfig;
  marketingFee: FeeConfig;
  contractManagementFee: FeeConfig;
  propertyManagementFee: FeeConfig;

  // Legacy fields (kept for backward compatibility)
  contractFee: string;
  contractFeePayer: Payer;
  managementFee: string;
  managementFeePayer: Payer;

  // Step 2: Payment Information
  /**
   * Determines how the entered totalAmount is interpreted for schedule generation.
   * 'term_total' (default): enteredAmount IS the full-term total (no proration).
   * 'annual': enteredAmount is a yearly rate; full-term total uses REGA 364-day proration.
   * PR2: persist this to contracts.rent_basis column.
   */
  rentBasis: RentBasis;
  paymentsCount: string;
  firstPaymentDate: string;
  preferredPaymentMethod: PaymentMethod | "";
  paymentFrequency: PaymentFrequency;
  // Only used when paymentFrequency === "custom". Each item is user-defined.
  customPaymentSchedule: PaymentScheduleItem[];

  // Step 3: Services
  electricity: UtilityConfig;
  water: UtilityConfig;
  gas: UtilityConfig;
  internet: UtilityConfig;
  maintenanceFee: string;
  maintenanceFeePayer: Payer;
  autoRenewal: boolean;
  noticePeriod: string;
  notes: string;
}

// Wizard steps are identified by KEY (not a fixed 1..4 index) because the
// active set now varies by contract type: rent/sublease keep all four, an
// off-plan sale drops Services, and an immediate sale drops both Payment and
// Services. Ordering + numbering are derived from the active-keys array in
// ContractWizardDrawer (see activeStepKeys), never hardcoded.
export type WizardStep = "info" | "payment" | "services" | "review";

// Bilingual titles for each step key.
export const STEP_META: Record<WizardStep, { label: string; labelEn: string }> = {
  info: { label: "معلومات العقد", labelEn: "Contract Info" },
  payment: { label: "معلومات الدفع", labelEn: "Payment Info" },
  services: { label: "الخدمات", labelEn: "Services" },
  review: { label: "المراجعة", labelEn: "Review" },
};

/**
 * The ordered list of ACTIVE step keys for a given contract type + sale type.
 *   - rent / sublease: info → payment → services → review
 *   - sale (off-plan): info → payment → review        (no annual services)
 *   - sale (immediate): info → review                 (no schedule, no services)
 * Off-plan follows the lease payment-schedule structure; an immediate sale
 * carries only a preferred payment channel on step 1 (spec item 1 + 5).
 */
export function activeStepKeys(
  contractType: ContractType,
  saleType: SaleType,
): WizardStep[] {
  if (contractType === "sale") {
    return saleType === "offplan"
      ? ["info", "payment", "review"]
      : ["info", "review"];
  }
  return ["info", "payment", "services", "review"];
}

export const CONTRACT_TYPES = [
  { value: "rent", label: "إيجار", labelEn: "Rent" },
  { value: "sale", label: "بيع", labelEn: "Sale" },
  { value: "sublease", label: "إيجار من الباطن", labelEn: "Sublease" },
  { value: "brokerage", label: "وساطة عقارية", labelEn: "Brokerage" },
] as const;

// Sale-structure selector (sale contracts only). "immediate" is the default
// (a normal transfer-of-ownership sale); "offplan" follows the lease payment
// schedule + delivery date.
export const SALE_TYPES = [
  { value: "immediate", label: "بيع مباشر", labelEn: "Immediate Sale" },
  { value: "offplan", label: "بيع على الخارطة", labelEn: "Off-plan" },
] as const;

export const DURATION_UNITS = [
  { value: "day", label: "يوم", labelEn: "Day" },
  { value: "month", label: "شهر", labelEn: "Month" },
  { value: "year", label: "سنة", labelEn: "Year" },
] as const;

export const PAYMENT_COUNTS = [
  { value: "1", label: "دفعة واحدة", labelEn: "1 Payment" },
  { value: "2", label: "دفعتين", labelEn: "2 Payments" },
  { value: "3", label: "3 دفعات", labelEn: "3 Payments" },
  { value: "4", label: "4 دفعات", labelEn: "4 Payments" },
  { value: "6", label: "6 دفعات", labelEn: "6 Payments" },
  { value: "12", label: "12 دفعة", labelEn: "12 Payments" },
] as const;

export const PAYMENT_METHODS = [
  { value: "cash", label: "نقدي", labelEn: "Cash" },
  { value: "bank_transfer", label: "تحويل بنكي", labelEn: "Bank Transfer" },
  { value: "cheque", label: "شيك", labelEn: "Cheque" },
  { value: "credit_card", label: "بطاقة ائتمان", labelEn: "Credit Card" },
  { value: "ejar_network", label: "شبكة إيجار", labelEn: "Ejar Network" },
] as const;

export const UTILITY_TYPES = [
  { value: "meter", label: "عداد", labelEn: "Meter" },
  { value: "fixed", label: "مبلغ ثابت", labelEn: "Fixed Amount" },
  { value: "included", label: "مشمول بالإيجار", labelEn: "Included in Rent" },
] as const;

export const UTILITY_TYPES_WITH_NA = [
  ...UTILITY_TYPES,
  { value: "not_available", label: "غير متوفر", labelEn: "Not Available" },
] as const;

export const PAYERS = [
  { value: "owner", label: "المالك", labelEn: "Owner" },
  { value: "tenant", label: "المستأجر", labelEn: "Tenant" },
] as const;

// Sale contracts use sale terminology: the owner-side party is the SELLER and
// the counterparty is the BUYER. Same underlying Payer values — labels only.
export const SALE_PAYERS = [
  { value: "owner", label: "البائع", labelEn: "Seller" },
  { value: "tenant", label: "المشتري", labelEn: "Buyer" },
] as const;

export type PayerOption = { readonly value: Payer; readonly label: string; readonly labelEn: string };

export function payerOptions(contractType: ContractType): readonly PayerOption[] {
  return contractType === "sale" ? SALE_PAYERS : PAYERS;
}

export const FEE_TYPES = [
  { value: "percentage", label: "نسبة مئوية %", labelEn: "Percentage %" },
  { value: "fixed", label: "مبلغ ثابت", labelEn: "Fixed Amount" },
] as const;

export const initialFormData: ContractWizardFormData = {
  contractType: "rent",
  saleType: "immediate",
  propertyId: "",
  unitId: "",
  // Start with one empty row. Wizard adds more via "Add another unit"; the
  // submit path normalizes to a single-unit insert if only one row is
  // populated and the rest of the form's behavior is unchanged.
  units: [{ ...emptyContractUnitRow, serviceFees: { ...emptyContractUnitRow.serviceFees } }],
  tenantId: "",
  startDate: "",
  durationValue: "12",
  durationUnit: "month",
  durationYears: "",
  durationMonths: "",
  durationDays: "",
  endDate: "",
  totalAmount: "",
  depositAmount: "",
  downPaymentType: "fixed",

  // New fee structure
  brokerageFee: { type: "percentage", value: "", payer: "tenant" },
  marketingFee: { type: "percentage", value: "", payer: "owner" },
  // Default to OWNER (was "tenant") so a new contract's management fee routes to
  // the owner and bundles with the owner's other invoices — consistent with
  // propertyManagementFee/managementFee. Operators can still override per-fee.
  contractManagementFee: { type: "percentage", value: "", payer: "owner" },
  propertyManagementFee: { type: "percentage", value: "", payer: "owner" },

  // Legacy
  contractFee: "",
  contractFeePayer: "tenant",
  managementFee: "",
  managementFeePayer: "owner",

  // The wizard's amount field is the ANNUAL rent (auto-summed from per-unit
  // "الإيجار السنوي / Annual rent"), so the canonical full-term total must
  // multiply the full years and prorate any partial period (deriveCanonicalTotal).
  // Defaulting to 'term_total' distributes the annual rate flat and under-bills
  // every contract longer than 12 months (2yr @ 10k/yr → 10k instead of 20k).
  rentBasis: "annual",
  paymentsCount: "4",
  firstPaymentDate: "",
  preferredPaymentMethod: "",
  paymentFrequency: "quarterly",
  customPaymentSchedule: [],
  electricity: { type: "meter", payer: "tenant" },
  water: { type: "meter", payer: "tenant" },
  gas: { type: "not_available", payer: "tenant" },
  internet: { type: "not_available", payer: "tenant" },
  maintenanceFee: "",
  maintenanceFeePayer: "owner",
  autoRenewal: false,
  noticePeriod: "30",
  notes: "",
};
